import React from 'react';
import type { ComponentProps } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { toast } from 'react-toastify';
import Styles from './Page.module.scss';
import { VendorLayoutV2 } from '~/components/common/layouts/VendorLayoutV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { PATH as DEALS_PATH } from '~/pages/vendors/deals';
import { PATH } from '~/pages/vendors/deals/[id]';
import { useDeals } from '~/hooks/vendors/useDeals';
import Loading from '~/components/common/parts/loading/Loading';
import { useDealInspectionReportImages } from '~/hooks/vendors/deals/useDealInspectionReportImages';
import { ReportImagesArea } from '~/components/common/reports/ReportImagesArea';
import {
  create as postInspectionReportImages,
  destroy,
  update,
  updateSeq,
  getDownloadPath as getDownloadImagePath,
  bulkDelete,
  bulkUpdate,
  getDownloadZipPath
} from '~/utils/api/vendors/deals/DealInspectionReportImage';
import { isSuccess } from '~/utils/api/api_handler';
import type {
  DealReportBulkUpdateParams,
  DealReportImage
} from '~/models/DealReportImage';
import {
  NUMBER_OF_DELETE_AT_ONCE,
  NUMBER_OF_UPLOAD_AT_ONCE
} from '~/models/DealReportImage';
import type { NskResponse } from '~/models/Api';
import { getDownloadPath as getDownloadPdfPath } from '~/utils/api/vendors/deals/DealInspectionReport';
import { ReportImagesAreaSp } from '~/components/common/reports/ReportImagesAreaSp';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { useSessions } from '~/hooks/vendors/useSessions';
import { create as postComplete } from '~/utils/api/vendors/deals/DealInspectionReportComplete';
import { isSubVendor } from '~/models/DealVendorCompany';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { DealLayoutFooter } from '~/components/common/layouts/deals/DealLayoutFooter';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { ReportImageUploadPanel } from '~/components/common/reports/ReportImageUploadPanel';

export const Page: React.FC = () => {
  const {
    isOpen: showConfirm,
    handleOpen: handleOpenConfirm,
    handleClose: handleCloseConfirm
  } = useOpenHandler();
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const [progress, setProgress] = React.useState<number>(0);
  const [selectedImageIds, setSelectedImageIds] = React.useState<
    ReadonlyArray<number>
  >([]);
  const { data: session } = useSessions().index();
  const router = useRouter();
  const id = useIdQueryHandler();
  const { data: deal } = useDeals().show(id);
  // globalMutateしたい
  const { data: reportImages, mutate: mutateReportImages } =
    useDealInspectionReportImages().index(id);
  if (deal === null || reportImages === null || session === null) {
    return <Loading isLoading />;
  }
  const items = [
    { label: '案件一覧', href: DEALS_PATH, as: Link },
    { label: '案件詳細', href: PATH(deal.id), as: Link },
    { label: '立会報告画像の登録' }
  ] as const satisfies ComponentProps<typeof DealLayout>['items'];
  const handleDrop = async (files: File[]): Promise<void> => {
    setProgress(0);
    setUploading(true);
    const arrFiles: File[][] = [];
    const totalFiles: number = files.length;
    let finishedFiles = 0;
    while (files.length > 0) {
      arrFiles.push(files.splice(0, NUMBER_OF_UPLOAD_AT_ONCE));
    }
    for await (const sendFiles of arrFiles) {
      const result = await postInspectionReportImages(sendFiles, deal.id);
      if (isSuccess(result)) {
        await mutateReportImages?.();
      } else {
        toast.error('エラーが発生しました');
        return;
      }
      finishedFiles += sendFiles.length;
      setProgress(Math.round((finishedFiles / totalFiles) * 100));
      await mutateReportImages?.();
    }
    setUploading(false);
    toast.success('画像を登録しました');
  };
  const handleUpdate = async (
    submitValue: DealReportImage
  ): Promise<NskResponse<void>> => update(submitValue);
  const handleBulkUpdate = async (
    submitValue: DealReportBulkUpdateParams
  ): Promise<void> => {
    const result = await bulkUpdate(deal.id, selectedImageIds, submitValue);
    if (isSuccess(result)) {
      await mutateReportImages?.();
      toast.success('更新しました');
    } else {
      toast.error('エラーが発生しました');
    }
  };
  const handleDestroy = async (
    reportImage: DealReportImage
  ): Promise<NskResponse<void>> => destroy(reportImage);
  const handleBulkDelete = async (): Promise<void> => {
    if (selectedImageIds.length === 0) {
      return;
    }
    setProgress(0);
    setDeleting(true);
    const imageIdArray: number[][] = [];
    const totalFiles: number = selectedImageIds.length;
    let finishedFiles = 0;
    for (
      let i = 0, j = selectedImageIds.length;
      i < j;
      i += NUMBER_OF_DELETE_AT_ONCE
    ) {
      imageIdArray.push(
        selectedImageIds.slice(i, i + NUMBER_OF_DELETE_AT_ONCE)
      );
    }
    for await (const ids of imageIdArray) {
      const result = await bulkDelete(deal.id, ids);
      if (isSuccess(result)) {
        await mutateReportImages?.();
      } else {
        toast.error('エラーが発生しました');
        return;
      }
      finishedFiles += ids.length;
      setProgress(Math.round((finishedFiles / totalFiles) * 100));
      await mutateReportImages?.();
    }
    setDeleting(false);
    setSelectedImageIds([]);
    toast.success('画像を削除しました');
  };
  const handleUpdateSeq = async (
    newOrderItems: DealReportImage[]
  ): Promise<void> => {
    updateSeq(newOrderItems, Number(id)).then();
  };
  const handleDownloadPdf = (): void => {
    const path = getDownloadPdfPath(deal.id);
    window.open(path);
  };
  const handleDownloadImage = (reportImage: DealReportImage): void => {
    const path = getDownloadImagePath(reportImage);
    window.open(path);
  };
  const handleDownloadZip = (): void => {
    const path = getDownloadZipPath(deal.id);
    window.open(path);
  };
  const handleSelectedDownloadZip = (): void => {
    const path = getDownloadZipPath(deal.id, selectedImageIds);
    window.open(path);
  };
  const handleSelectedDownloadPdf = (): void => {
    const path = getDownloadPdfPath(deal.id, selectedImageIds);
    window.open(path);
  };
  const handleSubmitComplete = async (): Promise<void> => {
    const result = await postComplete(deal.id);
    if (isSuccess(result)) {
      await router.push(PATH(deal.id));
      toast.success('完了しました');
      return;
    }
    toast.error(`エラーが発生しました。${result.message}`);
  };
  const onChangeSelectedImage = (selectedImageId: number): void => {
    if (selectedImageIds.includes(selectedImageId)) {
      setSelectedImageIds(
        selectedImageIds.filter((imageId) => imageId !== selectedImageId)
      );
    } else {
      setSelectedImageIds(selectedImageIds.concat([selectedImageId]));
    }
  };
  const handleResetSelectedIds = (): void => setSelectedImageIds([]);
  const handleSelectAllImages = (): void => {
    setSelectedImageIds(reportImages.map((image: DealReportImage) => image.id));
  };
  return (
    <VendorLayoutV2>
      <DealLayout deal={deal} isVendor items={items} noBackground>
        <div className={Styles.MainWrapper}>
          <h2 className={Styles.ReportTitle}>立会報告画像の登録</h2>
          <ReportImageUploadPanel
            deleting={deleting}
            handleDrop={handleDrop}
            progress={progress}
            uploading={uploading}
          />
          <ReportImagesArea
            companyType="vendor"
            handleBulkDelete={handleBulkDelete}
            handleBulkUpdate={handleBulkUpdate}
            handleDestroy={handleDestroy}
            handleDownloadImage={handleDownloadImage}
            handleDownloadPdf={handleDownloadPdf}
            handleDownloadZip={handleDownloadZip}
            handleResetSelectedIds={handleResetSelectedIds}
            handleSelectAllImages={handleSelectAllImages}
            handleSelectedDownloadPdf={handleSelectedDownloadPdf}
            handleSelectedDownloadZip={handleSelectedDownloadZip}
            handleUpdate={handleUpdate}
            handleUpdateSeq={handleUpdateSeq}
            onChangeSelectedImage={onChangeSelectedImage}
            reportImages={reportImages}
            selectedImageIds={selectedImageIds}
          />
          <ReportImagesAreaSp
            companyType="vendor"
            handleBulkDelete={handleBulkDelete}
            handleBulkUpdate={handleBulkUpdate}
            handleDestroy={handleDestroy}
            handleDownloadImage={handleDownloadImage}
            handleDownloadPdf={handleDownloadPdf}
            handleDownloadZip={handleDownloadZip}
            handleResetSelectedIds={handleResetSelectedIds}
            handleSelectAllImages={handleSelectAllImages}
            handleSelectedDownloadZip={handleSelectedDownloadZip}
            handleUpdate={handleUpdate}
            handleUpdateSeq={handleUpdateSeq}
            onChangeSelectedImage={onChangeSelectedImage}
            reportImages={reportImages}
            selectedImageIds={selectedImageIds}
          />
        </div>
        {selectedImageIds.length === 0 && (
          <DealLayoutFooter>
            {isSubVendor(deal, session.vendorCompany.id) ? (
              <button
                className={Styles.CompleteButton}
                onClick={handleOpenConfirm}
                type="button"
              >
                完了して通知する
              </button>
            ) : (
              <button
                className={Styles.CompleteButton}
                onClick={handleSubmitComplete}
                type="button"
              >
                完了して戻る
              </button>
            )}
            <ConfirmModal
              actionText="完了"
              confirmType="save"
              handleAccept={handleSubmitComplete}
              handleCancel={handleCloseConfirm}
              isOpen={showConfirm}
            >
              完了してよろしいですか？
            </ConfirmModal>
          </DealLayoutFooter>
        )}
      </DealLayout>
    </VendorLayoutV2>
  );
};
